import { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import { SOLUCOES_LABELS } from '@/pages/painel/lib/radarConstants';
import {
  type ProjetoEstrategico,
  type Marco,
  ESTAGIOS_PROJETO,
  ESTAGIO_LABELS,
  FAIXA_LABELS,
  FAIXA_TONES,
  MARGEM_TONES,
  formatBRL,
  formatPercent,
  formatDate,
} from '@/pages/painel/lib/comercial';

export default function ProjetosEstrategicos() {
  const { password } = useOutletContext<{ password: string }>();
  const [projetos, setProjetos] = useState<ProjetoEstrategico[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('comercial-admin', {
        body: { action: 'projetos', password },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as { projetos?: ProjetoEstrategico[]; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setProjetos(parsed?.projetos ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar projetos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password]);

  const totalValor = projetos.reduce((s, p) => s + Number(p.valor_potencial ?? 0), 0);
  const totalLucro = projetos.reduce((s, p) => s + Number(p.lucro_potencial ?? 0), 0);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-indigo-50 text-indigo-600 rounded-xl">
            <i className="ri-focus-3-line text-lg"></i>
          </div>
          <div>
            <h2 className="font-bold text-gray-900">Projetos Estratégicos</h2>
            <p className="text-sm text-gray-500">
              Acompanhamento de negócios relevantes, normalmente acima de R$ 200 mil.
            </p>
          </div>
        </div>
        <Link to="/painel/oportunidades" className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-700 px-3 py-2 rounded-lg hover:bg-indigo-50 cursor-pointer whitespace-nowrap">
          <i className="ri-rocket-2-line"></i> Promover de uma oportunidade
        </Link>
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Projetos</p>
          <p className="text-2xl font-bold text-gray-900">{projetos.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Valor total</p>
          <p className="text-lg font-bold text-gray-900 truncate">{formatBRL(totalValor)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Lucro potencial</p>
          <p className="text-lg font-bold text-green-700 truncate">{formatBRL(totalLucro)}</p>
        </div>
      </div>

      {loading && projetos.length === 0 ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : projetos.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-2xl border border-gray-200">
          <div className="w-14 h-14 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-3">
            <i className="ri-focus-3-line text-xl text-gray-400"></i>
          </div>
          <p className="text-sm text-gray-500 mb-1">Nenhum projeto estratégico ainda.</p>
          <p className="text-xs text-gray-400">Promova uma oportunidade relevante em <Link to="/painel/oportunidades" className="text-indigo-600 font-semibold">Oportunidades</Link>.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {projetos.map((p) => {
            const margemTone = p.margem_tone ? MARGEM_TONES[p.margem_tone] : null;
            return (
              <button key={p.id} onClick={() => setSelectedId(p.id)} className="text-left bg-white rounded-2xl border border-gray-200 p-5 hover:border-indigo-400/50 transition-colors cursor-pointer">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 leading-snug line-clamp-2">{p.titulo}</h3>
                    {p.empresa_nome && <p className="text-xs text-gray-500 truncate mt-0.5">{p.empresa_nome}</p>}
                  </div>
                  <span className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 border shrink-0 ${FAIXA_TONES[p.faixa]}`}>
                    {FAIXA_LABELS[p.faixa]}
                  </span>
                </div>

                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] font-semibold text-indigo-600 bg-indigo-50 rounded-full px-2 py-0.5">
                    {ESTAGIO_LABELS[p.estagio] ?? p.estagio}
                  </span>
                  {(p.cidade || p.uf) && <span className="text-xs text-gray-500">{[p.cidade, p.uf].filter(Boolean).join(' - ')}</span>}
                  {margemTone && (
                    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 border ${margemTone.tone}`}>
                      {formatPercent(p.margem)}
                    </span>
                  )}
                </div>

                {(p.solucoes?.length ?? 0) > 0 && (
                  <div className="mt-3 flex items-center gap-1 flex-wrap">
                    {(p.solucoes ?? []).slice(0, 3).map((s) => (
                      <span key={s} className="text-[10px] text-[#E8610A] bg-[#E8610A]/10 rounded-full px-2 py-0.5 whitespace-nowrap">
                        {SOLUCOES_LABELS[s] ?? s}
                      </span>
                    ))}
                  </div>
                )}

                <div className="mt-4 grid grid-cols-3 gap-2 text-center border-t border-gray-100 pt-3">
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider">Valor</p>
                    <p className="text-sm font-bold text-gray-900 truncate">{formatBRL(p.valor_potencial)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider">Lucro</p>
                    <p className="text-sm font-bold text-green-700 truncate">{formatBRL(p.lucro_potencial)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider">Fecha</p>
                    <p className="text-sm font-bold text-gray-900 truncate">{formatDate(p.previsao_fechamento)}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {selectedId && (
        <ProjetoDetalheModal projetoId={selectedId} password={password} onClose={() => setSelectedId(null)} onSaved={load} />
      )}
    </div>
  );
}

function ProjetoDetalheModal({ projetoId, password, onClose, onSaved }: { projetoId: number; password: string; onClose: () => void; onSaved: () => void }) {
  const [projeto, setProjeto] = useState<ProjetoEstrategico | null>(null);
  const [marcos, setMarcos] = useState<Marco[]>([]);
  const [estagio, setEstagio] = useState('');
  const [decisor, setDecisor] = useState('');
  const [arquiteto, setArquiteto] = useState('');
  const [construtora, setConstrutora] = useState('');
  const [newMarco, setNewMarco] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('comercial-admin', {
        body: { action: 'projeto_detalhar', password, projeto_id: projetoId },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as { projeto?: ProjetoEstrategico; marcos?: Marco[]; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setProjeto(parsed?.projeto ?? null);
      setMarcos(parsed?.marcos ?? []);
      setEstagio(parsed?.projeto?.estagio ?? '');
      setDecisor(parsed?.projeto?.decisor ?? '');
      setArquiteto(parsed?.projeto?.arquiteto ?? '');
      setConstrutora(parsed?.projeto?.construtora ?? '');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar projeto.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projetoId]);

  const salvar = async () => {
    setSaving(true);
    setError(null);
    setMsg(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('comercial-admin', {
        body: { action: 'projeto_salvar', password, projeto_id: projetoId, estagio, decisor, arquiteto, construtora },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as { ok?: boolean; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setMsg('Projeto atualizado.');
      onSaved();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao salvar.');
    } finally {
      setSaving(false);
    }
  };

  const addMarco = async () => {
    if (!newMarco.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await supabase.functions.invoke('comercial-admin', {
        body: { action: 'marco_salvar', password, projeto_id: projetoId, titulo: newMarco.trim() },
      });
      setNewMarco('');
      onSaved();
      await load();
    } catch {
      // silencioso
    } finally {
      setSaving(false);
    }
  };

  const toggleMarco = async (m: Marco) => {
    try {
      await supabase.functions.invoke('comercial-admin', {
        body: { action: 'marco_salvar', password, projeto_id: projetoId, marco_id: m.id, concluido: !m.concluido },
      });
      await load();
    } catch {
      // silencioso
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-3xl my-6 border border-gray-200">
        <div className="px-6 py-5 border-b border-gray-100 flex items-start justify-between gap-4 sticky top-0 bg-white rounded-t-2xl z-10">
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-gray-900 leading-snug">{projeto?.titulo}</h3>
            {projeto?.empresa_nome && <p className="text-sm text-gray-500">{projeto.empresa_nome}</p>}
          </div>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 cursor-pointer shrink-0">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="p-6 space-y-5">
          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}
          {msg && <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3">{msg}</div>}

          {loading && !projeto ? (
            <div className="flex justify-center py-10"><div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>
          ) : projeto ? (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-[11px] text-gray-500">Valor potencial</p>
                  <p className="text-sm font-bold text-gray-900">{formatBRL(projeto.valor_potencial)}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-[11px] text-gray-500">Lucro potencial</p>
                  <p className="text-sm font-bold text-green-700">{formatBRL(projeto.lucro_potencial)}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-[11px] text-gray-500">Probabilidade</p>
                  <p className="text-sm font-bold text-gray-900">{formatPercent(projeto.probabilidade)}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-[11px] text-gray-500">Fechamento</p>
                  <p className="text-sm font-bold text-gray-900">{formatDate(projeto.previsao_fechamento)}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <div>
                  <label className="text-[11px] text-gray-500 font-semibold">Estágio</label>
                  <select value={estagio} onChange={(e) => setEstagio(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white cursor-pointer focus:outline-none">
                    {ESTAGIOS_PROJETO.map((e) => <option key={e.key} value={e.key}>{e.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] text-gray-500 font-semibold">Decisor</label>
                  <input type="text" value={decisor} onChange={(e) => setDecisor(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none" />
                </div>
                <div>
                  <label className="text-[11px] text-gray-500 font-semibold">Arquiteto</label>
                  <input type="text" value={arquiteto} onChange={(e) => setArquiteto(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none" />
                </div>
                <div>
                  <label className="text-[11px] text-gray-500 font-semibold">Construtora</label>
                  <input type="text" value={construtora} onChange={(e) => setConstrutora(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none" />
                </div>
              </div>

              <div className="flex justify-end">
                <button onClick={salvar} disabled={saving} className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg px-4 py-2 cursor-pointer whitespace-nowrap disabled:opacity-60">
                  {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>} Salvar
                </button>
              </div>

              <section>
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Marcos / Milestones</h4>
                <div className="flex gap-2 mb-3">
                  <input type="text" value={newMarco} onChange={(e) => setNewMarco(e.target.value)} placeholder="Ex: Proposta enviada, reunião com decisor..." className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none" />
                  <button onClick={addMarco} disabled={saving || !newMarco.trim()} className="inline-flex items-center gap-1 text-sm font-semibold text-white bg-[#E8610A] hover:bg-[#C4500A] rounded-lg px-4 py-2 cursor-pointer whitespace-nowrap disabled:opacity-60">
                    <i className="ri-add-line"></i> Adicionar
                  </button>
                </div>
                {marcos.length === 0 ? (
                  <p className="text-xs text-gray-400">Nenhum marco ainda.</p>
                ) : (
                  <div className="space-y-1.5">
                    {marcos.map((m) => (
                      <div key={m.id} className="flex items-center gap-2 border border-gray-100 rounded-xl p-2.5">
                        <button onClick={() => toggleMarco(m)} className={`w-5 h-5 shrink-0 flex items-center justify-center border rounded-full cursor-pointer ${m.concluido ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300 text-transparent hover:border-green-500'}`}>
                          <i className="ri-check-line text-xs"></i>
                        </button>
                        <span className={`flex-1 text-sm ${m.concluido ? 'line-through text-gray-400' : 'text-gray-800'}`}>{m.titulo}</span>
                        {m.data_prevista && <span className="text-xs text-gray-400">{formatDate(m.data_prevista)}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}